import React from "react";
import useViewportWidth from "../hooks/useViewportWidth";
import simpleBackground from "../assets/simple_background.jpg";

/**
 * The "A Simple Way to Support Local" band between How It Works and the FAQ.
 *
 * On a desktop this is one flattened 2560x561 JPEG with its heading and paragraph baked into
 * the pixels, drawn at `maxHeight: 300px` — which is the design, and is left exactly as it
 * was. On a phone the same file is scaled to 390px wide, a 15% reduction, and the baked
 * heading lands at roughly 9px with the paragraph at 5px. That is not small copy, it is a
 * grey smudge, and no amount of cropping fixes it: the words are pixels.
 *
 * So below 900px the band is rebuilt out of real text over the same photograph.
 * AC-MOBILE-F14.
 */
function SupportLocal() {
  const isMobile = useViewportWidth() < 900;

  if (!isMobile) {
    return (
      <img
        src={simpleBackground}
        alt="Background Graphic"
        style={{
          width: "100%",
          maxHeight: "300px",
          objectFit: "cover",
        }}
      />
    );
  }

  return (
    <section
      style={{
        width: "100%",
        // border-box: the section is width:100% and its 20px of side padding would otherwise
        // be added on top, making it wider than the viewport (AC-MOBILE-F1).
        boxSizing: "border-box",
        padding: "clamp(36px, 10vw, 56px) 20px",
        textAlign: "center",
        // Two layers: a flat scrim over the photograph, so white copy holds against the pale
        // blurred half of it.
        //
        // The photograph carries its own heading and paragraph between x=540 and x=2020 of
        // 2560, and drawing live text on top of that would double every word. `474% auto`
        // sizes the image to 4.74x the band, so the band shows 1/4.74 = 21% of its width, and
        // `right center` puts that window at x=2020-2560 — the far glass, past the last
        // baked-in letter. Both numbers are fractions of the element, not pixels, so the
        // window stays on the same part of the photograph at any phone width.
        backgroundImage: `linear-gradient(rgba(22, 15, 8, 0.62), rgba(22, 15, 8, 0.62)), url(${simpleBackground})`,
        backgroundSize: "auto, 474% auto",
        backgroundPosition: "center, right center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <h2
        style={{
          margin: "0 0 12px",
          color: "#FFFFFF",
          fontSize: "clamp(24px, 6.6vw, 32px)",
          lineHeight: 1.2,
          fontWeight: 700,
        }}
      >
        A Simple Way to Support Local
      </h2>
      <p
        style={{
          margin: "0 auto",
          maxWidth: "460px",
          color: "rgba(255, 255, 255, 0.92)",
          fontSize: "clamp(15px, 4vw, 17px)",
          lineHeight: 1.55,
        }}
      >
        {/* Non-breaking space: the product name is one name and does not split across lines
            (AC-MOBILE-F15). */}
        Built on a secure AWS serverless backend with Stripe, Twilio, and Branch.io
        integrations, my{" "}HOOM lets you share beer — and moments — wherever you are.
        No app. No hassle.
      </p>
    </section>
  );
}

export default SupportLocal;
